import React, { FocusEventHandler } from "react"
import { RequiredSymbol } from "./various"
import { InputError } from "./inputError"
import { DropdownList,GroupByObject } from "./dropdownList"
import { isEqual } from "lodash"
import { StringsHelper } from "../stringsHelper"

export function ComboBoxWidget<T>(props: {
    selectedItem: T | null,
    allowNull: boolean | undefined,
    busy: boolean | undefined,
    readonly: boolean | undefined,
    onSelect: (item: T | null) => void;
    onToggle: () => void
}) {
    const { selectedItem, busy, readonly, allowNull } = props;
    const canClear = allowNull && selectedItem != null && !readonly;
    let component: React.ReactNode;
    if (busy) {
        component = <div style={{ textAlign: "center", cursor: "pointer" }}>
            <i title="Caricamento in corso" className="fas fa-sync fa-spin" />
        </div>
    }
    else {
        component = <>
            <a onClick={() => props.onSelect(null)}
                title="Rimuovi selezione"
                className="me-1"
                style={{ width: 10, textAlign: "center", cursor: "pointer", visibility: canClear ? undefined : "hidden", fontSize: "0.9em" }}>
                <i className="fas fa-times text-danger" />
            </a>
            <a
                onClick={readonly ? undefined : () => props.onToggle()}
                style={{ width: 10, textAlign: "center", cursor: readonly ? "not-allowed" : "pointer" }}>
                <i className="fas fa-chevron-down" />
            </a>
        </>
    }

    return <div className="d-flex justify-content-end align-items-center" style={{ width: 25 }}>
        {component}
    </div>
}


export function Combo<T>(): { new(): ComboBox<T> } {
    return ComboBox as any;
}
export function Grouped<T, TGroup>(): { new(): ComboBox<T, TGroup> } {
    return ComboBox as any;
}


interface ComboBoxProps<T, TGroup> {
    label?: string;
    data: Array<T>;
    error?: string;
    onSelect: (value: T | null) => void;
    itemComponent?: { (props: { item: T }): JSX.Element };
    value: T | null;
    textField: ((item: T) => string);
    placeholder?: string;
    readonly?: boolean;
    busy?: boolean;
    allowNull?: boolean;
    keepOpen?: boolean;
    groupBy?: GroupByObject<T, TGroup>;
    required?: boolean | undefined;
    title?: string;
    onBlur?: FocusEventHandler;
    onFocus?: FocusEventHandler;
    fromMultiComboBox?: boolean;
    searchFilter?: ((item: T, search: string) => boolean);
    style?: React.CSSProperties;
}

function getComponent<T, TGroup>(props: ComboBoxProps<T, TGroup>, item: T | null) {
    if (item == null) return null;
    if (props.itemComponent) {
        let Comp = props.itemComponent;
        return <Comp item={item} />
    }
    else return <span> {props.textField(item)}</span>
}

interface ComboBoxState<T> {
    filteredData: T[];
    internalItem: string | T | null;
    isOpen: boolean | null;
}

export class ComboBox<T, TGroup = void> extends React.Component<ComboBoxProps<T, TGroup>, ComboBoxState<T>>
{
    private container = React.createRef<HTMLDivElement>();
    private superparent = React.createRef<HTMLDivElement>();
    private input = React.createRef<HTMLInputElement>();
    private dropDownMenu = React.createRef<DropdownList<T, TGroup>>();

    constructor(props: ComboBoxProps<T, TGroup>) {
        super(props);
        this.state = {
            filteredData: this.props.data,
            internalItem: this.props.value,
            isOpen: null,
        };
    }

    private onChange = (value: string) => {
        if (!value && this.props.allowNull) {
            this.props.onSelect(null);
        }
        const filteredData = this.getFiltered(this.props, value)
        this.setState(
            {
                internalItem: value,
                filteredData,
                isOpen: filteredData.length > 0 ? true : this.state.isOpen,
            }, () => {
                if (value && this.state.filteredData.length > 0) {
                    const { index, item } = StringsHelper.BestMatch(this.state.filteredData, this.props.textField, value)
                    if (item != null)
                        this.dropDownMenu.current?.selectIndex(index);
                }
            });
    };

    private onSelect = (v: T | null) => {
        if (v == null) {
            if (this.props.allowNull) {
                this.props.onSelect(null);
            }
        } else {
            const value = this.props.data.find(x => isEqual(x, v));
            if (value != null) {
                if (this.state.internalItem !== v || this.props.fromMultiComboBox) {
                    this.props.onSelect(value);
                }
            }
        }
        const newState = { ...this.state };
        newState.isOpen = this.props.keepOpen || null;
        newState.internalItem = this.props.fromMultiComboBox ? null : v;
        if (v == null || this.props.fromMultiComboBox) newState.filteredData = this.props.data;
        this.setState(newState);
    };

    private textField = (item: string | T | null) => {
        if (typeof item === "string") {
            return item as string;
        }
        else {
            return item != null && this.props.textField(item) || "";
        }
    }

    private getFiltered(props: ComboBoxProps<T, TGroup>, value: T | string | null) {
        if (value == null) return props.data;
        const filterString = typeof value === "string" ? value.toLowerCase() : this.textField(value).toLowerCase();

        const exactMatch = props.data.find(x => this.textField(x).toLowerCase() == filterString);
        if (exactMatch)
            return [exactMatch];

        const { searchFilter } = props;
        if (searchFilter)
            return props.data.filter(x => searchFilter(x, filterString));
        else {
            const matches = StringsHelper.Matches(this.props.data, this.props.textField, filterString);
            return matches.map(x => x.item);
        }
    }

    private selectIfNeeded() {
        if (this.props.fromMultiComboBox)
            return;
        const { allowNull, data, value } = this.props;
        if (value == null) {
            if (!allowNull && data.length === 1) {
                this.onSelect(data[0]);
            }
        }
    }

    private onClickOutside = (e: MouseEvent) => {
        if (this.state.isOpen) {
            let clickedInside = false;
            this.superparent.current?.querySelectorAll("*").forEach(x => {
                if (x === e.target) {
                    clickedInside = true;
                }
            });
            if (!clickedInside) {
                this.setState({ isOpen: false })
            }
            else if (this.input.current != e.target) {
                e.preventDefault();
            }
        }
    }

    componentDidMount() {
        this.container.current!.addEventListener("keydown", this.handleKeyDown, false);
        document.addEventListener("mousedown", this.onClickOutside)
        this.selectIfNeeded();
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.onClickOutside);
        this.container.current!.removeEventListener("keydown", this.handleKeyDown, false);
    }

    componentDidUpdate(prevProps: ComboBoxProps<T, TGroup>, prevState: ComboBoxState<T>) {
        if (prevProps.value != this.props.value || !isEqual(prevProps.data, this.props.data)) {
            this.setState({
                internalItem: this.props.value,
                filteredData: this.props.data,
            }, () => {
                if (this.props.value) {
                    const matches = StringsHelper.Matches(this.props.data, this.props.textField, this.props.textField(this.props.value))[0];
                    if (matches) {
                        this.dropDownMenu.current?.selectIndex(matches.index);
                    }
                }
                else {
                    this.dropDownMenu.current?.selectIndex(0);
                }
            });
        }
    }

    handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
            case "ArrowUp":
                this.dropDownMenu.current?.moveUp();
                break;
            case "ArrowDown":
                this.dropDownMenu.current?.moveDown();
                break;
            case "Enter":
                e.preventDefault();
                this.dropDownMenu.current?.select();
                break;
            case "Escape":
                if (this.state.isOpen) {
                    this.setState({ isOpen: false, internalItem: null })
                    this.dropDownMenu.current?.resetSelection();
                }
                break;
            case "Tab":
                this.dropDownMenu.current?.select();
                break;
        }
    }

    private autoMatch = (input: string) => {
        const filtered = this.getFiltered(this.props, input);
        const match = input && filtered.length === 1;
        return match ? filtered[0] : null;
    }

    public toggle = () => {
        this.setState({ isOpen: !this.state.isOpen }, () => { if (this.state.isOpen) this.input.current?.focus() });
    }

    private onFocus = (event: React.FocusEvent) => {
        this.props.onFocus && this.props.onFocus(event);
        if (!this.state.isOpen)
            this.setState({ isOpen: true });
    }

    private onBlur = (event: React.FocusEvent) => {
        if (this.state.isOpen) {
            setTimeout(() => {
                if (!this.props.fromMultiComboBox) {
                    const autoSelectedItem = this.autoMatch(this.textField(this.state.internalItem));
                    this.onSelect(autoSelectedItem);
                }
                this.props.onBlur?.(event);
                this.setState({ isOpen: false })
            }, 300);
        }
    }

    private onClick = () => {
        if (this.input.current === document.activeElement && !this.state.isOpen && !this.props.readonly)
            this.setState({ isOpen: true });
    }



    render() {
        const { label, title, required, readonly, allowNull, busy, placeholder, error, style, value } = this.props;
        const { internalItem, isOpen, filteredData } = this.state;

        return (
            <div title={title} style={{ position: "relative" }} ref={this.superparent}>
                {label && <label className="form-label" onClick={e => this.input.current!.focus()}>{label}</label>} {required ? <RequiredSymbol /> : undefined}
                <div
                    ref={this.container}
                    className={`form-control bncombo ${isOpen ? "focused" : ""} ${readonly ? "readonly" : ""}`}
                    style={style}
                >
                    <input
                        type="text"
                        style={{ width: "100%", border: "none" }}
                        ref={this.input}
                        value={this.textField(internalItem)}
                        onChange={e => readonly ? undefined : this.onChange(e.target?.value)}
                        onFocus={readonly ? undefined : this.onFocus}
                        onBlur={readonly ? undefined : this.onBlur}
                        onClick={readonly ? undefined : this.onClick}
                        readOnly={readonly}
                        placeholder={placeholder}
                        autoComplete="chrome-off"
                    />
                    <ComboBoxWidget
                        busy={busy}
                        selectedItem={value}
                        allowNull={allowNull}
                        onSelect={this.onSelect}
                        onToggle={this.toggle}
                        readonly={readonly}
                    />
                </div>
                {filteredData && filteredData.length > 0 && <DropdownList
                    ref={this.dropDownMenu}
                    isOpen={isOpen || false}
                    items={filteredData}
                    onSelect={this.onSelect}
                    itemComponent={x => getComponent(this.props, x)}
                    groupBy={this.props.groupBy}
                />}
                <InputError error={error} />
            </div >
        );
    }
}

interface ResponsiveComboProps<T, TGroup = void> extends ComboBoxProps<T, TGroup> {
    idSelector: (item: T) => string;
}
export class SelectCombobox<T> extends React.PureComponent<ResponsiveComboProps<T>> {

    private onSelectEvent = (e: React.FormEvent<HTMLSelectElement>) => {
        var item = (this.props.data || []).find(x => this.props.idSelector(x) === (e.target as any).value);
        this.props.onSelect(item || null);
    }
    render() {
        const { idSelector, ...props } = this.props;
        return (
            <select
                className="form-control text-uppercase default-border"
                style={{ fontSize: "0.9em", height: 50 }}
                onChange={this.onSelectEvent}
                value={props.value && idSelector(props.value) || ""}
                disabled={props.readonly}>
                <option value="" disabled={Boolean(props.value)}>{props.placeholder}</option>
                {props.data.map(x => <option value={idSelector(x)} key={idSelector(x)}>{props.textField(x)}</option>)}
            </select>
        );
    }
}
export class ResponsiveCombobox<T> extends React.PureComponent<ResponsiveComboProps<T>> {
    cb: ComboBox<any>;

    public toggle() {
        this.cb.toggle();
    }

    private onSelectEvent = (e: React.FormEvent<HTMLSelectElement>) => {
        var item = (this.props.data || []).find(x => this.props.idSelector(x) === (e.target as any).value);
        this.props.onSelect(item || null);
    }
    render() {
        let CB = Combo<T>();
        const { idSelector, ...props } = this.props;
        return (
            <>
                <div className="d-none d-sm-block">
                    <CB ref={(cb: ComboBox<any>) => this.cb = cb} {...props} />
                </div>
                <div className="d-block d-sm-none">
                    <select className="form-control text-uppercase default-border"
                        style={{ fontSize: "0.9em", height: 50 }}
                        onChange={this.onSelectEvent}
                        value={this.props.value && this.props.idSelector(this.props.value) || ""}
                        disabled={this.props.readonly}>
                        <option value="" disabled={Boolean(this.props.value)}>{this.props.placeholder}</option>
                        {this.props.data.map(x => <option value={this.props.idSelector(x)} key={this.props.idSelector(x)}>{this.props.textField(x)}</option>)}
                    </select>
                </div>
            </>
        );
    }
}

export function ResponsiveCombo<T>(): { new(): ResponsiveCombobox<T> } {
    return ResponsiveCombobox as any;
}
